import React from 'react';
import { Helmet } from 'react-helmet';

/**
 * SEO Component for consistent metadata across all pages
 * @param {Object} props - SEO configuration object
 * @param {string} props.title - Page title
 * @param {string} props.description - Meta description
 * @param {string} props.canonical - Canonical URL
 * @param {string} props.ogTitle - Open Graph title
 * @param {string} props.ogDescription - Open Graph description
 * @param {string} props.ogImage - Open Graph image URL
 * @param {string} props.robots - Robots meta tag content
 * @param {Object} props.schema - JSON-LD structured data
 * @param {string} props.author - Article author (optional)
 * @param {string} props.publishedTime - Article published time (optional)
 * @param {string} props.modifiedTime - Article modified time (optional)
 * @param {string} props.section - Article section/topic (optional)
 */
const SEO = ({
  title,
  description,
  canonical,
  ogTitle,
  ogDescription,
  ogImage,
  robots = 'index, follow',
  schema,
  author,
  publishedTime,
  modifiedTime,
  section
}) => {
  // Ensure we have required fields
  if (!title || !description || !canonical) {
    console.warn('SEO component missing required props:', { title, description, canonical });
    return null;
  }

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="robots" content={robots} />

      {/* Canonical URL */}
      <link rel="canonical" href={canonical} />

      {/* Open Graph / Facebook */}
      <meta property="og:title" content={ogTitle || title} />
      <meta property="og:description" content={ogDescription || description} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:url" content={canonical} />
      <meta property="og:site_name" content="Twygs" />
      <meta property="og:type" content="website" />

      {/* Article-specific Open Graph tags */}
      {author && <meta property="article:author" content={author} />}
      {publishedTime && <meta property="article:published_time" content={publishedTime} />}
      {modifiedTime && <meta property="article:modified_time" content={modifiedTime} />}
      {section && <meta property="article:section" content={section} />}

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content="@twygs" />
      <meta name="twitter:title" content={ogTitle || title} />
      <meta name="twitter:description" content={ogDescription || description} />
      <meta name="twitter:image" content={ogImage} />

      {/* JSON-LD Structured Data */}
      {schema && <script type="application/ld+json">{JSON.stringify(schema)}</script>}
    </Helmet>
  );
};

export default SEO;
